import React, { useEffect, useMemo, useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { LayoutDashboard, Users, Building2, Wallet, UserCog, ScrollText, CreditCard } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { TID } from '@/constants/testIds';
import { Loader } from '@/components/Loader';

const links = [
  { to: '/admin', end: true, label: 'Tableau de bord', icon: LayoutDashboard },
  { to: '/admin/bailleurs', label: 'Bailleurs', icon: Building2 },
  { to: '/admin/locataires', label: 'Locataires', icon: Users },
  { to: '/admin/agents', label: 'Agents', icon: UserCog },
  { to: '/admin/abonnements', label: 'Abonnements', icon: Wallet },
  { to: '/admin/catalogue', label: 'Paramètres', icon: UserCog },
  { to: '/admin/audit', label: 'Audit', icon: ScrollText },
];

export default function AdminAbonnements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.listBailleurs()
      .then(({ data }) => setItems(data || []))
      .finally(() => setLoading(false));
  }, []);

  const activeCount = useMemo(() => items.filter((b) => b.subscription?.status === 'active').length, [items]);
  const monthlyRecurringCdf = activeCount * 3000;

  return (
    <AppLayout links={links} testid={TID.adminSidebar} title="Abonnements">
      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <div className="lb-card p-5"><div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Offre commerciale</div><div className="mt-2 text-2xl font-black text-[#0D1B2A]">3 000 FC / mois</div><div className="mt-1 text-xs text-slate-500">Locataires illimités</div></div>
        <div className="lb-card p-5"><div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Abonnements actifs</div><div className="mt-2 text-2xl font-black text-[#0D1B2A]">{activeCount}</div></div>
        <div className="lb-card p-5"><div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500"><CreditCard className="h-4 w-4 text-blue-600" /> MRR théorique</div><div className="mt-2 text-2xl font-black text-[#0D1B2A]">{monthlyRecurringCdf.toLocaleString('fr-FR')} FC</div><div className="mt-1 text-xs text-slate-500">Sur la base des abonnements actifs</div></div>
      </div>

      <div className="lb-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr className="text-left">
                {['Bailleur', 'Offre', 'Statut', 'Prix', 'Locataires', 'Expire le', 'Paiement'].map((h) => (
                  <th key={h} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-8"><Loader variant="full" /></td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">Aucun abonnement.</td></tr>}
              {items.map((b) => (
                <tr key={b.id} className="border-b border-slate-100">
                  <td className="px-4 py-3"><div className="font-semibold text-[#0D1B2A]">{b.prenom} {b.nom}</div><div className="text-xs text-slate-500">{b.email}</div></td>
                  <td className="px-4 py-3 font-semibold text-[#0D1B2A]">LoyerBook</td>
                  <td className="px-4 py-3"><Badge className="border border-emerald-200 bg-emerald-50 capitalize text-emerald-700 hover:bg-emerald-50">{b.subscription?.status || '—'}</Badge></td>
                  <td className="px-4 py-3 font-semibold text-[#0D1B2A]">3 000 FC/mois</td>
                  <td className="px-4 py-3 text-slate-700">{Number(b.locataires_count || 0)} / illimité</td>
                  <td className="px-4 py-3 text-slate-700">{b.subscription?.expires_at ? new Date(b.subscription.expires_at).toLocaleDateString('fr-FR') : '—'}</td>
                  <td className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">{b.subscription?.payment_method || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}
